import React from "react";
import LegalShell from "@/components/LegalShell";
import { Phone, Warning } from "@phosphor-icons/react";

export default function ResponsibleGambling() {
  return (
    <LegalShell title="Play Responsibly" subtitle="Daily Fantasy Sports carries a substantial risk of financial loss.">
      <div className="border-2 border-[#FFCC00] bg-[#1a1400] px-4 py-4">
        <div className="flex items-start gap-3">
          <Warning size={24} weight="fill" className="text-[#FFCC00] flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-white font-bold mb-1">If gambling is causing you or someone you know harm — get help now.</div>
            <div className="flex items-center gap-2 mt-2">
              <Phone size={18} weight="fill" className="text-[#39FF14]" />
              <a href="tel:1-800-426-2537" className="text-[#39FF14] text-lg font-black underline">
                1-800-GAMBLER
              </a>
              <span className="text-zinc-400 text-sm">— National Council on Problem Gambling, 24/7</span>
            </div>
            <div className="mt-1 text-sm text-zinc-400">
              Text <strong className="text-white">"HELPLINE"</strong> to <strong className="text-white">1-800-522-4700</strong> · Or visit{" "}
              <a href="https://www.ncpgambling.org" target="_blank" rel="noreferrer" className="text-[#39FF14] underline">ncpgambling.org</a>
            </div>
          </div>
        </div>
      </div>

      <Section title="Set limits before you play">
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li><strong className="text-white">Decide on a weekly DFS budget</strong> that you can lose without it affecting your rent, groceries, savings, or family obligations. Never exceed it.</li>
          <li><strong className="text-white">Never chase losses.</strong> If you lose a Sunday, do not enter Monday to "win it back."</li>
          <li><strong className="text-white">Never borrow money to enter contests.</strong></li>
          <li><strong className="text-white">Take breaks.</strong> If DFS is causing stress, disrupting sleep, or affecting relationships — step away.</li>
        </ul>
      </Section>

      <Section title="Warning signs of problem gambling">
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li>Spending more time or money on DFS than intended, week after week.</li>
          <li>Feeling irritable, anxious, or depressed when not playing.</li>
          <li>Lying to family or friends about how much you're spending.</li>
          <li>Chasing losses by increasing bet size.</li>
          <li>Missing work, school, or family obligations because of DFS.</li>
          <li>Feeling like DFS is the only thing that gives you a "high."</li>
        </ul>
      </Section>

      <Section title="Self-exclude from WarPaint DFS">
        <p>
          If you need to permanently step away from WarPaint DFS, email{" "}
          <a href="mailto:support@warpaintdfs.com" className="text-[#39FF14] underline">support@warpaintdfs.com</a>{" "}
          with the subject line <code className="bg-zinc-900 px-1.5 py-0.5 text-[#39FF14]">Self-Exclude</code> from the
          account email. We will:
        </p>
        <ol className="list-decimal ml-6 mt-2 space-y-1">
          <li>Cancel any active subscription immediately and issue a pro-rated refund.</li>
          <li>Permanently disable the account.</li>
          <li>Block the email address and payment method from creating any new account
              on WarPaint DFS for at least twelve (12) months.</li>
        </ol>
        <p className="mt-3">Self-exclusion is a serious step. It cannot be reversed for at
          least 12 months. If you are unsure, we recommend calling 1-800-GAMBLER first to
          speak with a trained counselor.</p>
      </Section>

      <Section title="DraftKings responsible-play tools">
        <p>
          WarPaint DFS is not affiliated with DraftKings, but DraftKings offers its own
          in-platform responsible-play features you should also use:
        </p>
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li>Deposit limits (daily / weekly / monthly)</li>
          <li>Entry-fee limits</li>
          <li>Session-time reminders</li>
          <li>Time-out and self-exclusion periods</li>
        </ul>
        <p className="mt-3">Configure them at <a href="https://www.draftkings.com/responsible-gaming" target="_blank" rel="noreferrer" className="text-[#39FF14] underline">draftkings.com/responsible-gaming</a>.</p>
      </Section>

      <Section title="State-specific resources">
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li><strong className="text-white">California:</strong> 1-800-GAMBLER · <a href="https://www.problemgambling.ca.gov" target="_blank" rel="noreferrer" className="text-[#39FF14] underline">problemgambling.ca.gov</a></li>
          <li><strong className="text-white">New York:</strong> 1-877-8-HOPENY (467369) · <a href="https://nyproblemgambling.org" target="_blank" rel="noreferrer" className="text-[#39FF14] underline">nyproblemgambling.org</a></li>
          <li><strong className="text-white">New Jersey:</strong> 1-800-GAMBLER · <a href="https://www.800gambler.org" target="_blank" rel="noreferrer" className="text-[#39FF14] underline">800gambler.org</a></li>
          <li><strong className="text-white">Massachusetts:</strong> 1-800-327-5050 · <a href="https://www.gamblinghelplinema.org" target="_blank" rel="noreferrer" className="text-[#39FF14] underline">gamblinghelplinema.org</a></li>
          <li><strong className="text-white">All other states:</strong> 1-800-GAMBLER (available 24/7)</li>
        </ul>
      </Section>

      <Section title="Where DFS is not permitted">
        <p>
          Paid-entry daily fantasy sports contests are currently prohibited in the following
          U.S. states (subject to change):{" "}
          <strong className="text-white">Arizona, Hawaii, Idaho, Louisiana, Montana, Nevada, and Washington.</strong>{" "}
          WarPaint DFS blocks account creation from these states. If you travel to one of these
          states, do not use the Service.
        </p>
      </Section>

      <Section title="Under 18? (or under 21 in MA / IA / LA)">
        You are not permitted to use WarPaint DFS, DraftKings, FanDuel, or any other DFS
        platform. If you are a parent or guardian and believe a minor has created an account,
        email <a href="mailto:support@warpaintdfs.com" className="text-[#39FF14] underline">support@warpaintdfs.com</a> and we will disable it immediately.
      </Section>
    </LegalShell>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-lg font-bold text-white mb-2">{title}</h2>
      <div className="text-sm text-zinc-300 leading-relaxed">{children}</div>
    </div>
  );
}
